import React, { useEffect, useState } from "react";
import "./business.css"; // shared styles
import { Link } from "react-router-dom";
import VideoPlayer from "../../components/VideoPlayer";
import { fetchEducationVideos } from "../../services/videoConfigService";

/* ======================================
   FALLBACK Hero video (if Firestore fails)
====================================== */
const FALLBACK_EDUCATION_INTRO = "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/AI%20videos%2FInsight%20center%2FInsight%20center%20Education.mp4?alt=media&token=23f05108-a432-405d-bcba-6a3e9382e37c";

const HERO = {
  poster:
    "https://images.unsplash.com/photo-1550439062-609e1531270e?q=80&w=1600&auto=format&fit=crop",
  videoUrl:
    "https://player.vimeo.com/video/880604427?h=243273a602&title=0&byline=0&portrait=0",
  mode: "modal",
};

/* ======================================
   Global render mode for cards + FAQ
   - "modal": popup player
   - "link" : open in new tab
====================================== */
const RENDER_MODE = "modal";

/* Helper to create distinct card objects */
const card = (title, thumb, videoUrl) => ({ title, thumb: thumb || "", videoUrl });

const FALLBACK_THUMB =
  "https://images.unsplash.com/photo-1541534401786-2077eed87a72?q=80&w=1200&auto=format&fit=crop";

/* ========= Modal Video Player (reused by hero, cards, FAQ) ========= */
function VideoModal({ open, onClose, src, title }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="biz-modal-backdrop" onClick={onClose}>
      <div className="biz-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="biz-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="biz-modal-body">
          <VideoPlayer
            title={title || "Video"}
            src={src}
            className="biz-modal-video"
            allowFullScreen
            controls
            autoPlay
          />
        </div>
      </div>
    </div>
  );
}

/* ========= Overlay (title top-left + play center) ========= */
function MediaOverlay({ title }) {
  return (
    <>
      <div
        className="biz-card-title-badge"
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 2,
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: ".02em",
          color: "#fff",
          padding: "6px 8px",
          borderRadius: 8,
          backdropFilter: "blur(2px)",
          background: "rgba(0,0,0,.55)",
          border: "1px solid rgba(255,255,255,.15)",
          boxShadow: "0 2px 10px rgba(0,0,0,.35)",
          maxWidth: "90%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={title}
      >
        {title}
      </div>
      <div
        className="biz-card-play"
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "999px",
            display: "grid",
            placeItems: "center",
            background:
              "radial-gradient(closest-side, #ff2d2d 60%, #a30f0f 100%)",
            color: "#fff",
            boxShadow: "0 10px 20px rgba(255,45,45,.35)",
            fontSize: 18,
            lineHeight: 1,
          }}
          aria-hidden="true"
        >
          ▶
        </div>
      </div>
    </>
  );
}

/* ========= Card (hero-style) ========= */
function VideoCard({ data }) {
  const [open, setOpen] = useState(false);
  const { title, thumb, videoUrl } = data;

  const openVideo = () => {
    if (RENDER_MODE === "link") {
      window.open(videoUrl, "_blank", "noopener,noreferrer");
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <article className="biz-card">
        <div
          className="biz-card-media"
          onClick={openVideo}
          style={{ position: "relative", cursor: "pointer" }}
          title={title}
        >
          <img src={thumb || FALLBACK_THUMB} alt={title} loading="lazy" />
          <MediaOverlay title={title} />
        </div>
      </article>
      <VideoModal
        open={open}
        onClose={() => setOpen(false)}
        src={videoUrl}
        title={title}
      />
    </>
  );
}

/* ========= Single-line Gallery Row with pagination ========= */
function GalleryRow({ title, cards }) {
  return (
    <section className="biz-row">
      <div className="biz-row-head">
        <div className="biz-row-dots" aria-hidden="true" />
        <h2 className="biz-row-title">{title}</h2>
        <div className="biz-row-dots" aria-hidden="true" />
      </div>
      <div className="biz-cards one-line">
        {cards.map((card, idx) => (
          <VideoCard key={idx} data={card} />
        ))}
      </div>
    </section>
  );
}

/* ========= Hero (same behavior as Business) ========= */
function Hero({ videoUrl }) {
  return (
    <section className="biz-hero small centered">
      <div className="biz-hero-inner centered-video">
        <div className="biz-hero-media biz-hero-video-wrapper">
          <VideoPlayer
            className="biz-hero-video"
            src={videoUrl || FALLBACK_EDUCATION_INTRO}
            title="Education intro video"
            controls
            playsInline
          />
        </div>
      </div>
    </section>
  );
}

/* ========= FAQ (video answers) ========= */
function FaqItem({ q, videoUrl }) {
  const [openModal, setOpenModal] = useState(false);

  const openVideo = () => {
    if (RENDER_MODE === "link") {
      window.open(videoUrl, "_blank", "noopener,noreferrer");
    } else {
      setOpenModal(true);
    }
  };

  return (
    <>
      <details className="biz-faq-item">
        <summary className="biz-faq-q">{q}</summary>
        <div className="biz-faq-a">
          <button className="biz-btn biz-btn-white" onClick={openVideo}>
            Watch answer
          </button>
        </div>
      </details>
      <VideoModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        src={videoUrl}
        title={q}
      />
    </>
  );
}

export default function Education(props) {
  const {hideButton} = props;
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const data = await fetchEducationVideos();
        setVideoData(data);
      } catch (error) {
        console.error("Failed to load education videos:", error);
      } finally {
        setLoading(false);
      }
    };
    loadVideos();
  }, []);

  if (loading) {
    return (
      <div className="biz-wrap" style={{ textAlign: 'center', padding: '2rem' }}>
        <p>Loading videos...</p>
      </div>
    );
  }

  // Map Firestore data to component structure
  const sections = videoData?.sections?.map(sec => ({
    id: sec.id,
    title: sec.title,
    cards: sec.videos?.map(v => card(v.title, v.thumbnail, v.url)) || []
  })) || [];

  const faqGeneral = videoData?.faqGeneral?.map(f => ({
    q: f.question,
    videoUrl: f.url
  })) || [];

  const faqAnnuities = videoData?.faqAnnuities?.map(f => ({
    q: f.question,
    videoUrl: f.url
  })) || [];

  const heroVideoUrl = videoData?.heroVideo?.url || FALLBACK_EDUCATION_INTRO;

  return (
    <div className="biz-wrap">
      {!hideButton && (
        <div className="ic-btn-row" style={{ paddingTop: 12 }}>
              <Link
                className="ic-btn"
                to="/learn/business"
                role="button"
                aria-label="Learn about Business Education"
              >
                <span className="ic-btn-label">Business Education</span>
              </Link>
              <Link
                className="ic-btn"
                to="/learn/education"
                role="button"
                aria-label="Learn about Financial Education"
              >
                <span className="ic-btn-label">Financial Education</span>
              </Link>
            </div>
          )}
      {/* HERO from Firestore */}
      <Hero videoUrl={heroVideoUrl} />

      {/* ROWS from Firestore */}
      {sections.slice(0, -1).map((sec) => (
        <GalleryRow key={sec.id} title={sec.title} cards={sec.cards} />
      ))}

      {/* FAQ (general) from Firestore */}
      {faqGeneral.length > 0 && (
        <section className="biz-faq">
          <h2 className="biz-faq-title">FAQ</h2>
          <div className="biz-faq-list">
            {faqGeneral.map((f, i) => (
              <FaqItem key={`g-${i}`} q={f.q} videoUrl={f.videoUrl} />
            ))}
          </div>
        </section>
      )}
      {sections.slice(-1).map((sec) => (
        <GalleryRow key={sec.id} title={sec.title} cards={sec.cards} />
      ))}
      {/* FAQ about annuities from Firestore */}
      {faqAnnuities.length > 0 && (
        <section className="biz-faq">
          <h2 className="biz-faq-title">FAQ about Annuities</h2>
          <div className="biz-faq-list">
            {faqAnnuities.map((f, i) => (
              <FaqItem key={`a-${i}`} q={f.q} videoUrl={f.videoUrl} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
